import { useEffect, useState } from "preact/hooks"
import { search } from "@nosto/search-js"
import { SearchProducts } from "@nosto/nosto-js/client"
import { autocompleteConfig, autocompleteProductsSize } from "@/config"

/**
 * The live autocomplete query gets its product fields filled in automatically by the store's
 * default-query step. A standalone search() call bypasses that, so without an explicit `fields`
 * list the backend only returns bare `productId` hits - list the fields the product card needs.
 */
const productFields = [
  "productId",
  "url",
  "name",
  "imageUrl",
  "imageHash",
  "thumbUrl",
  "brand",
  "availability",
  "price",
  "priceText",
  "listPrice",
  "priceCurrencyCode",
  "customFields.key",
  "customFields.value",
  "alternateImageUrls",
  "skus.id",
  "skus.imageUrl",
  "skus.availability"
]

/**
 * Best sellers are only worth surfacing if they can actually be bought, so out of stock
 * products and cheap accessories are filtered out before sorting by units sold.
 * The availability value must match the facet value used by the SERP "Hide Out of Stock"
 * toggle. Set `minPrice` to 0 to drop the price constraint.
 */
const minPrice = 30

const productFilter = [
  { field: "availability", value: ["In Stock"] },
  ...(minPrice > 0 ? [{ field: "price", range: [{ gte: String(minPrice) }] }] : [])
]

/**
 * Fetches best-selling products independently of the live autocomplete query,
 * for display before the user starts typing.
 */
export function usePopularProducts(enabled: boolean) {
  const [popularProducts, setPopularProducts] = useState<SearchProducts>()

  useEffect(() => {
    if (!enabled) {
      return
    }
    search(
      {
        products: {
          size: autocompleteProductsSize,
          sort: [{ field: "stats.buys", order: "desc" }],
          filter: productFilter,
          emptyQueryMatchesAll: true,
          fields: productFields
        }
      },
      { hitDecorators: autocompleteConfig.search.hitDecorators, useMemoryCache: true }
    ).then(result => setPopularProducts(result.products))
  }, [enabled])

  return popularProducts
}
